/**
 * CRM: AS REGRAS DA ESCRITA (11/10/2026, medição do CRM, PR-1 do padrão). Funções puras que as telas usam e os testes
 * cobram dos dois lados.
 */
import { fmtDate, getTodayStr } from './formatters';

type Proposta = { code: string; status: string; validUntil?: string | null };

/**
 * P1: o status da proposta saiu do <select> cru (qualquer transição, inclusive Aceita → Rascunho, com um "Alterar
 * status?" que não dizia nada). Agora só as transições que fazem sentido de onde a proposta está, cada uma com a
 * consequência. Enviada/Visualizada vêm do envio e do link, não de um clique; Aceita não volta para trás.
 */
export type Transicao = 'Aceita' | 'Recusada' | 'Cancelada' | 'Rascunho';
export function transicoesDaProposta(status: string): Transicao[] {
  if (status === 'Aceita') return [];
  if (['Cancelada', 'Recusada', 'Expirada'].includes(status)) return ['Rascunho'];
  return ['Aceita', 'Recusada', 'Cancelada'];
}

export const ROTULO_DA_TRANSICAO: Record<Transicao, string> = {
  Aceita: '✅ Aprovar (aceite pelo operador)',
  Recusada: '✕ Marcar como recusada',
  Cancelada: '🚫 Cancelar',
  Rascunho: '🔄 Reabrir (volta a Rascunho)',
};

export function confirmacaoDeTransicao(p: Proposta, para: Transicao) {
  const base = { message: `${p.code}: de "${p.status}" para "${para}".` };
  switch (para) {
    case 'Aceita':
      return {
        ...base,
        title: 'Aprovar a proposta pelo operador?',
        consequence: 'Fica "Aceita" com o seu usuário no histórico, SEM o registro do aceite do cliente (nome, CPF, IP). Ela some do MRR potencial e libera a criação da assinatura.',
        confirmLabel: 'Aprovar',
      };
    case 'Recusada':
      return {
        ...base,
        title: 'Marcar a proposta como recusada?',
        consequence: 'Sai do MRR potencial do lead. O motivo da recusa não fica registrado por este caminho; o do cliente vem pelo link.',
        confirmLabel: 'Marcar como recusada',
      };
    case 'Cancelada':
      return {
        ...base,
        title: 'Cancelar a proposta?',
        consequence: 'Sai do MRR potencial do lead e fica registrada no histórico. Dá para reabrir depois.',
        confirmLabel: 'Cancelar proposta',
        cancelLabel: 'Voltar',
        danger: true,
      };
    default:
      return {
        ...base,
        title: 'Reabrir a proposta?',
        consequence: 'Volta para Rascunho e conta de novo no MRR potencial do lead.',
        confirmLabel: 'Reabrir',
      };
  }
}

/** P4: o motivo do servidor, não "Erro ao salvar". */
export function motivoDoServidor(e: any): string {
  const msg = e?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (msg) return String(msg);
  if (e?.request && !e?.response) return 'o servidor não respondeu';
  return e?.message || 'erro desconhecido';
}

/**
 * P2: excluir o lead. A FK proposals→leads é SET NULL e a de briefings é CASCADE (conferido em produção): as propostas
 * ficam órfãs, somem do CRM e os links públicos continuam abertos; o briefing é apagado. O confirm dizia só
 * "Exclusão DEFINITIVA" ou "Deseja excluir X?".
 */
export type ImpactoDaExclusao = { propostas: Proposta[] | null; temBriefing: boolean | null };
export function confirmacaoDeExcluirLead(nome: string, impacto: ImpactoDaExclusao, hoje: string = getTodayStr()) {
  const { propostas, temBriefing } = impacto;
  if (propostas === null) {
    return {
      recusa: `Não excluí ${nome}: não consegui ler as propostas dele, e não excluo sem saber o que fica órfão.`,
    };
  }
  const aceitas = propostas.filter((p) => p.status === 'Aceita');
  // 12/10/2026 (conferência em tela): contava pelo status só, e dizia "ainda pode ser aceita" de proposta VENCIDA, que a
  // página pública já não deixa aceitar (podeDecidirNoLink: Enviada/Visualizada dentro da validade). A conta é a mesma regra.
  const vivas = propostas.filter((p) => podeDecidirNoLink(p, hoje));
  const partes: string[] = [];
  if (propostas.length) {
    partes.push(`${propostas.length} proposta${propostas.length === 1 ? '' : 's'} (${propostas.map((p) => p.code).join(', ')}) fica${propostas.length === 1 ? '' : 'm'} sem lead: some${propostas.length === 1 ? '' : 'm'} do CRM, e os links públicos seguem abertos.`);
  } else {
    partes.push('O lead não tem proposta.');
  }
  if (vivas.length) partes.push(`${vivas.length} ainda pode${vivas.length === 1 ? '' : 'm'} ser aceita${vivas.length === 1 ? '' : 's'} pelo cliente.`);
  partes.push(temBriefing === null ? 'O briefing, se houver, é apagado.' : temBriefing ? 'O briefing preenchido é apagado.' : 'Não há briefing.');
  const prefiraArquivar = aceitas.length > 0 || vivas.length > 0;
  return {
    title: `Excluir o lead ${nome}?`,
    message: partes.join(' '),
    consequence: prefiraArquivar
      ? `Prefira Arquivar: ${aceitas.length ? 'há proposta aceita' : 'há link que o cliente ainda pode aceitar'}, e excluir apaga o vínculo sem fechar o link.`
      : 'Exclusão definitiva, sem volta.',
    confirmLabel: prefiraArquivar ? 'Excluir mesmo assim' : 'Excluir',
    cancelLabel: 'Voltar',
    danger: true,
  };
}

/** P3: Converter em Cliente pergunta o que vai gravar; a razão social da Receita vence a digitada, e isso é dito. */
export function confirmacaoDeConverter(dados: { companyName: string; cnpj: string; dueDay: number; emailFinancial: string; emailGeneral: string }, razaoDigitada: string) {
  const trocouRazao = !!razaoDigitada && razaoDigitada.trim() !== dados.companyName.trim();
  return {
    title: 'Criar o cliente a partir do lead?',
    message: [
      `Razão social: ${dados.companyName}${trocouRazao ? ` (da Receita; no lead estava "${razaoDigitada}")` : ''}.`,
      `CNPJ: ${dados.cnpj || 'sem CNPJ'}.`,
      `Vencimento: dia ${dados.dueDay}.`,
      `E-mail financeiro e geral: ${dados.emailFinancial}${dados.emailGeneral !== dados.emailFinancial ? ` / ${dados.emailGeneral}` : ''}.`,
    ].join(' '),
    consequence: 'O cliente nasce Ativo com esses dados; vencimento e e-mails se acertam depois no cadastro dele.',
    confirmLabel: 'Criar cliente',
  };
}

/**
 * P6: a página pública só oferece Aceitar/Recusar em Enviada ou Visualizada, dentro da validade. Antes oferecia também
 * em Rascunho e ignorava `validUntil`.
 */
const diaCivil = (v: string | null | undefined) => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v || ''));
  return m ? m[1] : null;
};
export function podeDecidirNoLink(p: Pick<Proposta, 'status' | 'validUntil'>, hoje: string = getTodayStr()): boolean {
  if (!['Enviada', 'Visualizada'].includes(p.status)) return false;
  const ate = diaCivil(p.validUntil);
  return !ate || ate >= hoje;
}

/**
 * Marcar como enviada fora do e-mail (L1 e G1 do Heitor, 28/10/2026). Caso real: a PRO20260093 foi mandada por WhatsApp
 * em Rascunho e o cliente não achou o Aceitar (P6 acima). Só Rascunho dentro da validade. A trava é a do link: proposta
 * com versão gerada pelo HUB, nenhuma publicada e sem documento externo cairia no layout nativo, que o gate não viu.
 * `versoesGeradas` vem do backend; ausente vale 0, que é o comportamento de antes da trava.
 */
export type MarcarComoEnviada = { pode: boolean; motivo: 'nao_e_rascunho' | 'travada' | null; frase: string | null };
export const FRASE_DA_TRAVA_DO_GATE = 'Rascunho com versão gerada pelo HUB e nada publicado. Publique a versão aprovada em Gerar com IA › Versões para poder marcar como enviada.';
export function marcarComoEnviada(
  p: Pick<Proposta, 'status' | 'validUntil'> & { versoesGeradas?: number | null; currentDocumentId?: number | null; customHtmlS3Key?: string | null; customPdfS3Key?: string | null },
  hoje: string = getTodayStr(),
): MarcarComoEnviada {
  if (estadoVisivelDaProposta(p, hoje) !== 'Rascunho') return { pode: false, motivo: 'nao_e_rascunho', frase: null };
  const gerada = Number(p.versoesGeradas) > 0;
  if (gerada && !p.currentDocumentId && !p.customHtmlS3Key && !p.customPdfS3Key) return { pode: false, motivo: 'travada', frase: FRASE_DA_TRAVA_DO_GATE };
  return { pode: true, motivo: null, frase: null };
}

/** O que a página pública diz quando não oferece a decisão (Aceita e Recusada já têm o comprovante). */
export function fraseDoLinkSemDecisao(p: Pick<Proposta, 'status' | 'validUntil'>, hoje: string = getTodayStr()): string | null {
  if (p.status === 'Aceita' || p.status === 'Recusada') return null;
  if (p.status === 'Cancelada') return 'Esta proposta foi cancelada. Fale com a IPCOM para uma versão atualizada.';
  if (p.status === 'Rascunho') return 'Esta proposta ainda não foi enviada para aceite.';
  const ate = diaCivil(p.validUntil);
  if (p.status === 'Expirada' || (ate && ate < hoje)) {
    return `Esta proposta venceu${ate ? ` em ${fmtDate(ate)}` : ''}. Fale com a IPCOM para uma versão atualizada.`;
  }
  return null;
}

// ======================= PR-1b do CRM: as decisões de estrutura (Heitor, 12/10/2026) =======================

/** E1: as ações de assinatura moram na etapa Geração Assinatura (e seguem no Ganho de quem já fechou antes). */
export const ETAPAS_DA_ASSINATURA = ['Geração Assinatura', 'Ganho'];

/** E4: proposta viva = a que soma no valor estimado do lead (o backend: updateLeadValues). */
export const PROPOSTA_MORTA = ['Recusada', 'Cancelada', 'Expirada'];
export type ValorDoLead =
  | { editavel: false; motivo: 'lendo' | 'erro' | 'derivado'; frase: string }
  | { editavel: true; frase: string };
export function valorEstimadoDoLead(propostas: { status: string }[] | null, erro: string | null): ValorDoLead {
  if (erro) return { editavel: false, motivo: 'erro', frase: `Não consegui ler as propostas do lead (${erro}): o valor não é editável agora.` };
  if (propostas === null) return { editavel: false, motivo: 'lendo', frase: 'Lendo as propostas do lead…' };
  const vivas = propostas.filter((p) => !PROPOSTA_MORTA.includes(p.status)).length;
  if (vivas > 0) return { editavel: false, motivo: 'derivado', frase: `${vivas === 1 ? 'Soma da proposta viva' : `Soma das ${vivas} propostas vivas`}; mude nas propostas.` };
  return { editavel: true, frase: 'Sem proposta viva: valor manual.' };
}

/** E5: a proposta aceita é travada; o caminho para mudar é uma nova versão. */
export function fraseDaPropostaTravada(p: { acceptedByName?: string | null; acceptedAt?: string | null }): string {
  const quem = p.acceptedByName ? ` por ${p.acceptedByName}` : '';
  const quando = p.acceptedAt ? ` em ${fmtDate(p.acceptedAt)}` : '';
  return `Proposta aceita${quem}${quando}: o conteúdo está travado. Para mudar itens, valores ou condições, duplique como nova versão.`;
}

/** E6: os dados da empresa vêm do lead (o backend sobrescreve na edição); na proposta, só leitura. */
export const FRASE_DOS_DADOS_DO_LEAD = 'Vêm do lead: edite no lead.';

/** E7: o aceite pelo operador registra quem aceitou e por qual canal. */
export const CANAIS_DO_ACEITE = ['e-mail', 'telefone', 'WhatsApp', 'presencial'] as const;
export type CanalDoAceite = typeof CANAIS_DO_ACEITE[number];
export function porqueNaoAprovar(nome: string, canal: string): string | null {
  if (!nome.trim()) return 'Diga o nome de quem aceitou.';
  if (!(CANAIS_DO_ACEITE as readonly string[]).includes(canal)) return 'Diga por qual canal o aceite veio.';
  return null;
}

/**
 * Totais do modal do lead (12/10/2026, conferência em tela). O valor estimado vem do backend como texto (`numeric` do
 * Postgres: "1500.00"), e `ps + mrr * 12` virava concatenação: "1500.00" + 54228 = "1500.0054228", exibido como
 * R$ 1.500,01 no "Valor Total" e na "Assinatura 12 meses". Converte antes de somar.
 */
export function totaisDoLead(ps: unknown, mrr: unknown) {
  const num = (v: unknown) => { const n = typeof v === 'number' ? v : parseFloat(String(v ?? '')); return Number.isFinite(n) ? n : 0; };
  const mrrAnual = num(mrr) * 12;
  return { mrrAnual, total: num(ps) + mrrAnual };
}

// ======================= Bloco C da revisão (12/10/2026): a proposta vencida =======================

/** Status em que a proposta ainda está VIVA (pode ser enviada, aceita ou virar contrato) se a validade não passou. */
const VIVA_SE_VALIDA = ['Rascunho', 'Enviada', 'Visualizada'];

/**
 * C2: o estado que manda é o vencido. O selo dizia "Visualizada" e, ao lado, "⚠ Expirada"; agora o selo É "Expirada".
 * A fronteira é a da página pública (podeDecidirNoLink): vale ATÉ o dia da validade, inclusive.
 */
export function estadoVisivelDaProposta(p: Pick<Proposta, 'status' | 'validUntil'>, hoje: string = getTodayStr()): string {
  if (p.status === 'Expirada') return 'Expirada';
  const ate = diaCivil(p.validUntil);
  return VIVA_SE_VALIDA.includes(p.status) && ate && ate < hoje ? 'Expirada' : p.status;
}

/**
 * C2: as ações seguem o estado. Expirada mostra Link e Duplicar; Enviar e Gerar Contrato só quando cabem (proposta viva
 * e dentro da validade; Gerar Contrato também na Aceita, que é para isso que ela existe).
 */
export function acoesDaProposta(p: Pick<Proposta, 'status' | 'validUntil'>, hoje: string = getTodayStr()) {
  const estado = estadoVisivelDaProposta(p, hoje);
  const expirada = estado === 'Expirada';
  return {
    expirada,
    enviar: !expirada && VIVA_SE_VALIDA.includes(estado),
    // "✨ Gerar com IA" só na proposta viva. A CANCELADA oferecia (16/10/2026): o botão olhava só Aceita e Recusada.
    gerarComIa: !expirada && VIVA_SE_VALIDA.includes(estado),
    gerarContrato: !expirada && (VIVA_SE_VALIDA.includes(estado) || estado === 'Aceita'),
  };
}

/**
 * C6: editar proposta vencida avisa, e o salvar exige validade nova. Retorna o aviso enquanto a validade escolhida não
 * passa de hoje; null quando a proposta não venceu ou já tem validade nova.
 */
export function avisoDaPropostaVencida(original: Pick<Proposta, 'status' | 'validUntil'> | null, validadeEscolhida: string, hoje: string = getTodayStr()): string | null {
  if (!original || estadoVisivelDaProposta(original, hoje) !== 'Expirada') return null;
  const nova = diaCivil(validadeEscolhida);
  if (nova && nova >= hoje) return null;
  const venceu = diaCivil(original.validUntil);
  return `Esta proposta venceu${venceu ? ` em ${fmtDate(venceu)}` : ''}; ao salvar, defina a nova validade.`;
}

/**
 * A1 (12/10/2026, bloco A da revisão): as seis etapas do funil, na ordem. Ganho, Perdido, Congelado e Arquivado são
 * desfechos e ficam fora da trilha (moram no ⋯ do selo).
 */
export const ETAPAS_DO_FUNIL = ['Novo', 'Contato', 'POC', 'Proposta', 'Negociação', 'Geração Assinatura'];

export type PassoDaTrilha = { etapa: string; estado: 'feita' | 'atual' | 'proxima' };

/**
 * A trilha lê o status SALVO do lead, nunca o do formulário: ela grava na hora, e mostrar a escolha não salva como se
 * fosse a etapa seria a tela mentindo. Ganho conta como o funil inteiro feito; desfecho fora do funil, nenhuma atual.
 */
export function trilhaDoLead(statusSalvo: string | null | undefined, etapas: string[] = ETAPAS_DO_FUNIL): PassoDaTrilha[] {
  const atual = statusSalvo === 'Ganho' ? etapas.length : etapas.indexOf(statusSalvo || etapas[0] || 'Novo');
  return etapas.map((etapa, i) => ({
    etapa,
    estado: atual < 0 ? 'proxima' : i < atual ? 'feita' : i === atual ? 'atual' : 'proxima',
  }));
}

/** A1: clicar numa etapa da trilha pergunta e grava na hora (PATCH /status, com de→para no histórico). */
export function confirmacaoDeMover(nome: string, de: string, para: string) {
  return {
    title: `Mover ${nome} para ${para}?`,
    message: `Hoje está em ${de}.`,
    consequence: 'Grava na hora e entra no histórico do lead.',
    confirmLabel: `Mover para ${para}`,
  };
}

/** A3: Marcar como Ganho, pelo ⋯ do selo. */
export function confirmacaoDeGanho(nome: string) {
  return {
    title: `Marcar ${nome} como Ganho?`,
    consequence: 'Sai do funil e vai para Ganhos. Dá para voltar pelo Status da ficha. Para gerar a assinatura, use a etapa Geração Assinatura.',
    confirmLabel: 'Marcar como Ganho',
  };
}

/**
 * B4 (12/10/2026, bloco B da revisão): o cabeçalho da coluna diz o que a etapa faz, no molde da Central ("A fazer ·
 * cobra prazo e lembra"). REGRA do Maestro: o texto só afirma o que o sistema FAZ de verdade nessa etapa; quando o
 * sistema não faz nada, diz o que a etapa significa. Conferido no código em 12/10: o único comportamento por etapa é o
 * da Geração Assinatura (a ficha oferece gerar a assinatura e converter em cliente, ETAPAS_DA_ASSINATURA). Não há
 * lembrete de validade nem cobrança de retorno: o único cron de lead é o que descongela no prazo.
 */
export const O_QUE_A_ETAPA_FAZ: Record<string, string> = {
  'Novo': 'entrou, sem conversa ainda',
  'Contato': 'primeira conversa feita',
  'POC': 'cliente testando',
  'Proposta': 'proposta com o cliente',
  'Negociação': 'ajustando condições',
  'Geração Assinatura': 'a ficha gera a assinatura e o cliente',
};

// ======================= Bloco B da revisão (12/10/2026): o que trazer da Central do Fábio =======================

/** B2: Meus · Da equipe · Todos, no lugar do select por pessoa (o "Minhas / Pedi para outros / Todas" do Fábio). */
export type EscopoDoResponsavel = 'meus' | 'equipe' | 'todos';
const temResponsavel = (l: any) => !!(l?.assignedToId || l?.assignedToUser?.name || l?.assignedTo);
export function casaEscopo(l: any, escopo: EscopoDoResponsavel, meuId: number | string | null | undefined): boolean {
  if (escopo === 'todos') return true;
  const meu = meuId != null && meuId !== '' && String(l?.assignedToId) === String(meuId);
  return escopo === 'meus' ? meu : temResponsavel(l) && !meu;
}

/**
 * B6: visto ou não visto, no card, pela PROPOSTA (o cliente abriu o link?). Olha a proposta enviada mais recente do lead
 * (Enviada ou Visualizada); sem proposta enviada, não há o que dizer. O visto do lead pelo responsável não existe no
 * backend e fica de fora.
 */
export type VistoDoLead = { visto: true; em: string } | { visto: false } | null;
export function vistoDoLead(propostasDoLead: any[]): VistoDoLead {
  const enviadas = (propostasDoLead || []).filter((p) => p && ['Enviada', 'Visualizada'].includes(p.status));
  if (!enviadas.length) return null;
  const quando = (p: any) => String(p.sentAt || p.createdAt || '');
  const ultima = [...enviadas].sort((a, b) => quando(b).localeCompare(quando(a)))[0];
  return ultima.viewedAt ? { visto: true, em: String(ultima.viewedAt) } : { visto: false };
}

/**
 * B5 (12/10/2026, bloco B): a anotação rápida do board. Só o nome ou só o CNPJ; Enter salva como Novo e o resto se
 * completa depois na ficha. Contrato da #410 do backend: contato e e-mail viram opcionais na criação, mas o companyName
 * CONTINUA obrigatório, então quem digita só o CNPJ manda o texto digitado também como companyName.
 */
export function payloadDaAnotacao(texto: string): { companyName: string; status: 'Novo'; cnpj?: string } | null {
  const t = String(texto || '').trim();
  if (!t) return null;
  const digitos = t.replace(/\D/g, '');
  const soCnpj = digitos.length === 14 && /^[\d.\-/\s]+$/.test(t);
  return soCnpj ? { companyName: t, cnpj: digitos, status: 'Novo' } : { companyName: t, status: 'Novo' };
}

/**
 * A recusa da anotação. Com o backend de hoje (sem a #410), criar sem contato e e-mail volta 400 de validação: a tela
 * diz isso com todas as letras e aponta o caminho que funciona hoje, em vez de repetir o erro cru do validador.
 */
export function fraseDaRecusaDaAnotacao(e: any): string {
  const m = e?.response?.data?.message;
  const texto = (Array.isArray(m) ? m.join(' ') : String(m || '')).toLowerCase();
  if (e?.response?.status === 400 && /contactname|email|contato|e-mail/.test(texto)) {
    return 'o servidor ainda exige contato e e-mail para criar lead. Por enquanto, use "Novo Lead".';
  }
  return motivoDoServidor(e);
}

// ======================= A4 (12/10/2026): as etapas do funil vêm do servidor (#409 do backend) =======================

/**
 * A fonte ÚNICA das etapas no front: board, trilha da ficha, select de Status, funil do dashboard e "etapa da
 * assinatura" leem daqui. Compatível: backend sem a #409 (404 em GET /leads/funnel-stages) cai nas seis fixas, como
 * sempre foi; leitura que falha por outro motivo também cai nelas, mas a tela diz que não leu (`erro`).
 */
export type EtapasDoFunil = {
  ativas: string[];
  assinatura: string | null;
  terminais: string[];
  aviso: string | null;
  origem: 'servidor' | 'fixa';
  erro?: string;
};
export const ETAPAS_FIXAS: EtapasDoFunil = {
  ativas: ETAPAS_DO_FUNIL,
  assinatura: 'Geração Assinatura',
  terminais: ['Ganho', 'Perdido', 'Congelado', 'Arquivado'],
  aviso: null,
  origem: 'fixa',
};
export function etapasDaResposta(r: any): EtapasDoFunil {
  const ativas = Array.isArray(r?.etapasAtivas) ? r.etapasAtivas.filter((e: any) => typeof e === 'string' && e) : [];
  if (!ativas.length) return { ...ETAPAS_FIXAS, erro: 'a resposta não trouxe etapas ativas' };
  return {
    ativas,
    assinatura: typeof r.etapaDaAssinatura === 'string' && r.etapaDaAssinatura ? r.etapaDaAssinatura : null,
    terminais: Array.isArray(r.estadosTerminais) && r.estadosTerminais.length ? r.estadosTerminais : ETAPAS_FIXAS.terminais,
    aviso: typeof r.avisoDaAssinatura === 'string' && r.avisoDaAssinatura ? r.avisoDaAssinatura : null,
    origem: 'servidor',
  };
}
/** Onde moram as ações de assinatura (E1): a etapa marcada como da assinatura, e o Ganho de quem já fechou. */
export function etapasDaAssinaturaDe(f: EtapasDoFunil): string[] {
  return [f.assinatura, 'Ganho'].filter((e): e is string => !!e);
}
/** O select de Status da ficha: as etapas ativas e os desfechos que ele sempre ofereceu (Congelar tem caminho próprio). */
export function statusDoSelect(f: EtapasDoFunil): string[] {
  return [...f.ativas, 'Ganho', 'Perdido', 'Arquivado'];
}

/** A4 (editor em Configurações): uma linha da lista de etapas, como o PUT /leads/funnel-stages espera. */
export type EtapaEditavel = { id?: number; nome: string; ativa: boolean; etapaDaAssinatura: boolean };

/**
 * O corpo do PUT. A ORDEM é a posição na lista (1, 2, 3…): ordem e "etapa da assinatura" são propriedades do conjunto,
 * por isso a lista vai completa (contrato da #409). Nome sem espaço nas pontas.
 */
export function payloadDasEtapas(lista: EtapaEditavel[]) {
  return lista.map((e, i) => ({
    ...(e.id ? { id: e.id } : {}),
    nome: String(e.nome || '').trim(),
    ordem: i + 1,
    ativa: !!e.ativa,
    etapaDaAssinatura: !!e.etapaDaAssinatura,
  }));
}

/**
 * A recusa do PUT, dita como o servidor manda: a frase pronta (`message`) e uma linha por problema
 * (ETAPAS_DO_FUNIL_INVALIDAS → `problemas`) ou por impedimento (ETAPAS_DO_FUNIL_EM_USO → `impedimentos`, que conta os
 * leads na etapa E os congelados que voltam para ela). Outra falha: a frase de sempre.
 */
export function recusaDasEtapas(e: any): { frase: string; linhas: string[] } {
  const d = e?.response?.data || {};
  if (d.code === 'ETAPAS_DO_FUNIL_INVALIDAS' || d.code === 'ETAPAS_DO_FUNIL_EM_USO') {
    const linhas = (Array.isArray(d.problemas) ? d.problemas : Array.isArray(d.impedimentos) ? d.impedimentos : []).map(String);
    return { frase: String(d.message || 'O servidor recusou as etapas.'), linhas };
  }
  return { frase: motivoDoServidor(e), linhas: [] };
}

// ======================= B1 (12/10/2026): a próxima ação com prazo (#411 do backend) =======================

/**
 * O backend tem a próxima ação? Pela CHAVE no lead, não pelo valor: com a #411 o lead traz `nextActionText` (null ou
 * texto) em toda listagem; sem ela, a chave não existe. Chave ausente não é null: é o servidor que ainda não sabe.
 */
export function temProximaAcao(leads: any[]): boolean {
  return (leads || []).some((l) => l && Object.prototype.hasOwnProperty.call(l, 'nextActionText'));
}

/** Dias de atraso da próxima ação (0 = vence hoje ou a vencer; null = sem ação ou concluída). Dia civil, sem UTC. */
export function atrasoDaProximaAcao(l: any, hoje: string = getTodayStr()): number | null {
  const ate = diaCivil(l?.nextActionAt);
  if (!l?.nextActionText || !ate || l?.nextActionDoneAt) return null;
  if (ate >= hoje) return 0;
  const [a1, m1, d1] = ate.split('-').map(Number);
  const [a2, m2, d2] = hoje.split('-').map(Number);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86400000);
}

/**
 * O PUT do lead não leva `nextActionDoneAt`: o campo NÃO está no DTO (de propósito, na #411: concluir é o PATCH
 * próprio, que carimba e limpa numa operação só), e mandá-lo arriscaria um 400 do validador.
 */
export function semCamposSoDeLeitura<T extends Record<string, any>>(campos: T): Omit<T, 'nextActionDoneAt'> {
  const { nextActionDoneAt: _feito, ...resto } = campos;
  return resto;
}

/** A recusa do concluir (LEAD_SEM_PROXIMA_ACAO, PROXIMA_ACAO_JA_CONCLUIDA): a frase pronta do servidor. */
export function fraseDaRecusaDoConcluir(e: any): string {
  const d = e?.response?.data || {};
  return (d.code === 'LEAD_SEM_PROXIMA_ACAO' || d.code === 'PROXIMA_ACAO_JA_CONCLUIDA') && d.message ? String(d.message) : motivoDoServidor(e);
}

// ======================= Ficha do lead em quatro abas (16/10/2026, aprovado pelo Heitor) =======================

/** As abas da ficha do lead, na ordem da tela. */
export type AbaDoLead = 'dados' | 'propostas' | 'contrato' | 'historico';
const ABAS_DO_LEAD: AbaDoLead[] = ['dados', 'propostas', 'contrato', 'historico'];
/** `?ficha=`: o `?aba=` já é das abas do CRM (Pipeline, Ganhos, Perdidos). Dados não vai para a URL. */
export function abaDoLeadDaUrl(v: string | null | undefined): AbaDoLead | null {
  return v && v !== 'dados' && (ABAS_DO_LEAD as string[]).includes(v) ? (v as AbaDoLead) : null;
}

/** Proposta que não conta mais: fora da soma e recolhida na aba Propostas. */
export const PROPOSTA_ENCERRADA = ['Recusada', 'Cancelada', 'Expirada'];

/**
 * A aba em que a ficha abre, pela ETAPA: a etapa é onde o operador está.
 *  - etapa da assinatura (a MARCA do funil, não o nome) e Ganho → Contrato, se já houver contrato; sem contrato,
 *    Propostas, porque o "Gerar Contrato" mora na proposta aceita e abrir numa aba vazia seria pior;
 *  - Proposta e Negociação → Propostas;
 *  - o resto (Novo, Contato, POC, Perdido, congelado, etapa nova sem marca) → Dados.
 * `temContrato` null = a leitura dos contratos não chegou: decide como sem contrato (a aba Propostas mostra o vínculo).
 */
export function abaInicialDoLead(status: string | null | undefined, funil: EtapasDoFunil, temContrato: boolean | null): AbaDoLead {
  const s = String(status || '');
  if (etapasDaAssinaturaDe(funil).includes(s)) return temContrato ? 'contrato' : 'propostas';
  if (s === 'Proposta' || s === 'Negociação') return 'propostas';
  return 'dados';
}

/** O que os rótulos das abas dizem. `null` = não li: o rótulo não mostra número (nunca um 0 inventado). */
export type ResumoDaFicha = { vivas: number | null; contratos: number | null; assinados: number; assinantes: number };
export function resumoDaFicha(propostas: any[] | null, contratos: any[] | null): ResumoDaFicha {
  const vivo = (contratos || []).find((c: any) => c && c.status !== 'Cancelado');
  const assinantes: any[] = Array.isArray(vivo?.signers) ? vivo.signers : [];
  return {
    vivas: propostas ? propostas.filter((p: any) => !PROPOSTA_ENCERRADA.includes(p?.status)).length : null,
    contratos: contratos ? contratos.length : null,
    assinados: assinantes.filter((s: any) => s?.status === 'signed').length,
    assinantes: assinantes.length,
  };
}
/**
 * O alerta de validade do cartão da proposta. Só a proposta que ainda espera resposta (Rascunho, Enviada, Visualizada)
 * "vence em breve" (7 dias). Produção (16/10/2026, lead 86): as CANCELADAS apareciam com "⏰ Expira em breve" e barra
 * âmbar, e a Aceita também podia — proposta que já teve desfecho não expira.
 */
export function alertaDeValidade(p: Pick<Proposta, 'status' | 'validUntil'>, hoje: string = getTodayStr()): 'venceu' | 'vence-em-breve' | null {
  if (estadoVisivelDaProposta(p, hoje) === 'Expirada') return 'venceu';
  if (!VIVA_SE_VALIDA.includes(p.status)) return null;
  const ate = diaCivil(p.validUntil);
  if (!ate) return null;
  const limite = new Date(`${hoje}T12:00:00Z`);
  limite.setUTCDate(limite.getUTCDate() + 7);
  return ate <= limite.toISOString().slice(0, 10) ? 'vence-em-breve' : null;
}

/** "2/4 assinaturas" enquanto falta assinatura no contrato vivo; o número de contratos depois; nada sem contrato. */
export function seloDoContrato(r: ResumoDaFicha): { texto: string; pendente: boolean } | null {
  if (!r.contratos) return null;
  if (r.assinantes > 0 && r.assinados < r.assinantes) return { texto: `${r.assinados}/${r.assinantes} assinaturas`, pendente: true };
  return { texto: String(r.contratos), pendente: false };
}
